import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ErrorText } from "../components/ui/feedback";
import { Input } from "../components/ui/Input";
import { PageHeader } from "../components/ui/PageHeader";
import { adminApi, type LifecycleAction } from "./api";
import { EditWorkspaceModal } from "./EditWorkspaceModal";
import { InviteSoundGuyModal } from "./InviteSoundGuyModal";
import { QueryError } from "./dense";
import { TenantTable } from "./TenantTable";
import { TransferOwnershipModal } from "./TransferOwnershipModal";
import { ADMIN_KEY, adminWorkspacesQuery } from "./queries";
import type { AdminWorkspace, WorkspaceStatus } from "./types";

type Dialog =
  | { kind: "edit"; workspace: AdminWorkspace }
  | { kind: "transfer"; workspace: AdminWorkspace }
  | { kind: "delete" | "restore"; workspace: AdminWorkspace }
  | { kind: "invite" }
  | null;

/** "" is All. The order follows the operator's most-used filters first. */
const FILTERS: readonly (WorkspaceStatus | "")[] = [
  "",
  "active",
  "suspended",
  "deleted",
  "pending",
];

/** Operator-admin workspace list: status filter, search, and per-workspace actions. */
export function TenantsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<WorkspaceStatus | "">("");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState<Dialog>(null);

  const workspaces = useQuery(
    adminWorkspacesQuery({ status: status || undefined, q: q.trim() || undefined }),
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: [...ADMIN_KEY, "workspaces"] });

  const lifecycle = useMutation({
    mutationFn: ({ id, action }: { id: string; action: LifecycleAction }) =>
      adminApi.runLifecycle(id, action),
    onSuccess: () => {
      setDialog(null);
      return refresh();
    },
  });

  const actAs = useMutation({
    mutationFn: (workspaceId: string) => adminApi.startActingAs(workspaceId),
    onSuccess: async (_result, workspaceId) => {
      // The acting flag lives on the workspace query; drop any cached copy so the banner is not stale.
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] });
      return navigate({ to: "/app/$workspaceId", params: { workspaceId } });
    },
  });

  const onLifecycle = (workspace: AdminWorkspace, action: LifecycleAction) => {
    lifecycle.mutate({ id: workspace.id, action });
  };

  const onConfirmLifecycle = () => {
    if (dialog?.kind !== "delete" && dialog?.kind !== "restore") return;
    lifecycle.mutate({
      id: dialog.workspace.id,
      action: dialog.kind === "delete" ? "delete" : "restore",
    });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title={t("admin.workspaces.title")}
        actions={
          <Button variant="primary" onClick={() => setDialog({ kind: "invite" })}>
            {t("admin.invite.open")}
          </Button>
        }
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div
          role="group"
          aria-label={t("admin.filter.status")}
          className="flex max-w-full gap-1 overflow-x-auto rounded-control bg-ice-2 p-1 [scrollbar-width:none]"
        >
          {FILTERS.map((value) => {
            const selected = status === value;
            return (
              <button
                key={value || "all"}
                type="button"
                aria-pressed={selected}
                onClick={() => setStatus(value)}
                className={`min-h-9 shrink-0 rounded-[8px] px-3 text-note font-semibold whitespace-nowrap transition-colors duration-[120ms] ${
                  selected ? "bg-board text-blue shadow-board" : "text-ink-2 hover:text-ink"
                }`}
              >
                {t(`admin.status.${value || "all"}`)}
              </button>
            );
          })}
        </div>
        <Input
          type="search"
          aria-label={t("admin.search")}
          placeholder={t("admin.search")}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          className="md:max-w-64"
        />
      </div>

      <ErrorText error={lifecycle.error ?? actAs.error} />
      {workspaces.isError ? (
        <QueryError error={workspaces.error} onRetry={() => void workspaces.refetch()} />
      ) : null}
      {workspaces.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
      {workspaces.data ? (
        <TenantTable
          workspaces={workspaces.data}
          busy={lifecycle.isPending || actAs.isPending}
          onEdit={(workspace) => setDialog({ kind: "edit", workspace })}
          onTransfer={(workspace) => setDialog({ kind: "transfer", workspace })}
          onDelete={(workspace) => setDialog({ kind: "delete", workspace })}
          onRestore={(workspace) => setDialog({ kind: "restore", workspace })}
          onSuspend={(workspace) => onLifecycle(workspace, "suspend")}
          onUnsuspend={(workspace) => onLifecycle(workspace, "unsuspend")}
          onActAs={(workspace) => actAs.mutate(workspace.id)}
        />
      ) : null}

      {dialog?.kind === "edit" ? (
        <EditWorkspaceModal
          workspace={dialog.workspace}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void refresh();
          }}
        />
      ) : null}
      {dialog?.kind === "transfer" ? (
        <TransferOwnershipModal
          workspace={dialog.workspace}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            void refresh();
          }}
        />
      ) : null}
      {dialog?.kind === "invite" ? <InviteSoundGuyModal onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "delete" || dialog?.kind === "restore" ? (
        <ConfirmDialog
          title={t(dialog.kind === "delete" ? "admin.delete.title" : "admin.restore.title")}
          message={t(dialog.kind === "delete" ? "admin.delete.message" : "admin.restore.message", {
            name: dialog.workspace.name,
          })}
          confirmLabel={t(dialog.kind === "delete" ? "common.delete" : "common.restore")}
          variant={dialog.kind === "delete" ? "danger" : "primary"}
          busy={lifecycle.isPending}
          error={lifecycle.error}
          onCancel={() => setDialog(null)}
          onConfirm={onConfirmLifecycle}
        />
      ) : null}
    </div>
  );
}
